import React, { useState } from "react";
import { Button, FilePickerWithPreview } from "src/components";
import { Box } from "src/components/Box";
import { Add } from "@asphalt-react/iconpack";
import { useTranslation } from "src/hooks/useTranslation";
import styled from "styled-components";
import { MediaQueries } from "src/constants";
import { observer } from "mobx-react-lite";
import { useBusinessInfoState } from "./BusinessInfo.state";
import { usePassportStore } from "src/stores/Passport.store";
import { composeFiles } from "src/helpers/composeFiles";
import { UploadTooltipModal } from "./BusinessInfo.upload.tooltip";
import { errFormatter } from "src/helpers/errorFormatter";
import { eventDispatcher } from "src/helpers/eventEmitter";

const WrapperEl = styled.div`
  ${MediaQueries.screen.medium.minWidth} {
    display: grid;
    grid-template-rows: 1fr auto;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: ${(p) => p.theme.dimension.spacing.xl}px;
    grid-row-gap: ${(p) => p.theme.dimension.spacing.s}px;
  }
`;

const AddMoreButtonEl = styled(Button)`
  grid-row: last-line;
  justify-self: start;
`;

const removeItem = (array: any, value: any): any => {
  const index = array.indexOf(value);
  if (index > -1) {
    array.splice(index, 1);
  }
  return array;
};

type BusinessInfoFilePickerProps = {
  purpose: "akta" | "menkeh";
  maxFiles: number;
};

export const BusinessInfoFilePicker = observer(
  (props: BusinessInfoFilePickerProps) => {
    const [showModal, setShowModal] = useState(false);
    const [uploadingKeys, setUploadingKeys] = useState<number[]>([]);
    const [errorKeys, setErrorKeys] = useState<number[]>([]);
    const translate = useTranslation();
    const state = useBusinessInfoState();
    const store = usePassportStore();
    const { skFileUploaderCount, aktaFileUploaderCount, setFile } = state;
    const { authUser } = store;
    const fileUploaders: React.ReactNode[] = [];
    let counter =
      props.purpose === "akta" ? aktaFileUploaderCount : skFileUploaderCount;
    const analyticsEventLabel = props.purpose === "akta" ? "akta" : "sk";

    const _removeUploadingKey = (key: number) => {
      setUploadingKeys(removeItem(uploadingKeys, key));
    };

    const _removeErrorKey = (key: number) => {
      setErrorKeys(removeItem(errorKeys, key));
    };

    const _deleteFile = (fileName: string, counter: number) => () => {
      fileUploaders.splice(counter - 1, 1);
      state.deleteFile(fileName);
    };

    const isLoading = (counter: number): boolean => {
      return (
        uploadingKeys.includes(counter) &&
        state.loadingState?.has.uploadFileBusinessInfo
      );
    };

    const isError = (counter: number): boolean => {
      return (
        errorKeys.includes(counter) &&
        state.errorState?.has.uploadFileBusinessInfo
      );
    };

    const incrementUploader = () => {
      if (props.purpose === "akta") {
        state.aktaFileUploaderCount += 1;
      } else {
        state.skFileUploaderCount += 1;
      }
    };

    const uploadedFilesCount: number = state.form?.files?.filter(
      (file) => file?.name?.includes(props.purpose) && !!file.url
    )?.length!;
    const showAddMoreButton =
      counter < props.maxFiles && uploadedFilesCount === counter;

    const uploadFile = (counter: number) => async (file: File) => {
      _removeErrorKey(counter);
      setUploadingKeys((keys) => [...keys, counter]);
      try {
        const fetchUrl = await state.uploadFileBusinessInfo(
          authUser.id,
          file,
          state.getAvailableFileName(props.purpose)
        );
        const newFile = setFile(
          state.getAvailableFileName(props.purpose),
          file,
          fetchUrl
        );
        state.setValue("files", composeFiles(state?.form?.files!, newFile));
        eventDispatcher.publish({
          action: `ob_upload_${analyticsEventLabel}_success`,
        });
      } catch (e) {
        setErrorKeys((errors) => [...errors, counter]);
        const { status, message } = errFormatter(e);
        eventDispatcher.publish({
          action: `ob_upload_${analyticsEventLabel}_error`,
          payload: {
            fileType: file.type,
            message: message,
            httpStatus: status,
          },
        });
      }
      _removeUploadingKey(counter);
    };

    while (counter > 0) {
      fileUploaders.push(
        <Box marginTopL key={state.previewUrl(props.purpose)?.[counter - 1]}>
          <FilePickerWithPreview
            onClick={() =>
              eventDispatcher.publish({
                action: `ob_upload_${analyticsEventLabel}`,
              })
            }
            onChange={uploadFile(counter)}
            isLoading={isLoading(counter)}
            previewUrl={state.previewUrl(props.purpose)?.[counter - 1]}
            metadata={
              state.getFilesByName(props.purpose)?.[counter - 1]?.metadata
            }
            mediaType={
              state.getFilesByName(props.purpose)?.[counter - 1]?.media_type
            }
            isError={isError(counter)}
            onDelete={_deleteFile(
              state.getFilesByName(props.purpose)?.[counter - 1]?.name!,
              counter
            )}
            onError={(error) =>
              eventDispatcher.publish({
                action: `ob_upload_${analyticsEventLabel}_error`,
                payload: {
                  message: error.message,
                },
              })
            }
            onInfoClick={() => setShowModal(true)}
          />
        </Box>
      );
      counter--;
    }

    return (
      <>
        <WrapperEl>
          {fileUploaders.reverse()}
          {showAddMoreButton && (
            <AddMoreButtonEl
              onClick={incrementUploader}
              key={`${props.purpose} File Uploader ${counter}`}
              disabled={isLoading(counter)}
              secondary
              padding0
            >
              <Add />
              &nbsp; &nbsp;
              {translate("business_info.upload.add_more")}
            </AddMoreButtonEl>
          )}
        </WrapperEl>
        <UploadTooltipModal
          showModal={showModal}
          onClose={() => setShowModal(false)}
          purpose={props.purpose}
        />
      </>
    );
  }
);
